import React from "react";

/**
 * SkeletonDashboard - Loading skeleton for dashboard pages
 * Provides visual feedback while data is loading
 */

const SkeletonCard = ({ className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
        <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/4"></div>
        </div>
    </div>
);

const SkeletonMetricCard = () => (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
            <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/4"></div>
        </div>
    </div>
);

const SkeletonChart = ({ height = "h-64" }) => (
    <div className={`bg-white rounded-lg shadow-sm p-6 ${height}`}>
        <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="flex items-end justify-between h-48 space-x-2">
                {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="flex-1 bg-gray-200 rounded" style={{ height: `${Math.random() * 60 + 20}%` }}></div>
                ))}
            </div>
        </div>
    </div>
);

const SkeletonTable = () => (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex space-x-4">
                        <div className="h-4 bg-gray-200 rounded flex-1"></div>
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const SkeletonDashboard = ({ type = "default" }) => {
    switch (type) {
        case "analytics":
            return (
                <div className="space-y-6 p-6">
                    {/* Header */}
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    </div>

                    {/* Metric Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <SkeletonMetricCard key={i} />
                        ))}
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <SkeletonChart />
                        <SkeletonChart />
                    </div>

                    {/* Table */}
                    <SkeletonTable />
                </div>
            );

        case "stripe":
            return (
                <div className="space-y-6 p-6">
                    {/* Header */}
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <SkeletonChart height="h-80" />
                        <SkeletonChart height="h-80" />
                    </div>

                    {/* Transaction Table */}
                    <SkeletonTable />
                </div>
            );

        case "users":
            return (
                <div className="space-y-6 p-6">
                    {/* Header */}
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    </div>

                    {/* Filters */}
                    <div className="flex space-x-4">
                        <div className="h-10 bg-gray-200 rounded w-32"></div>
                        <div className="h-10 bg-gray-200 rounded w-40"></div>
                        <div className="h-10 bg-gray-200 rounded w-24"></div>
                    </div>

                    {/* User Table */}
                    <SkeletonTable />
                </div>
            );

        default:
            return (
                <div className="space-y-6 p-6">
                    {/* Header */}
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    </div>

                    {/* Content Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                </div>
            );
    }
};

export default SkeletonDashboard;
